import * as React from "react";
import { useContext, useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { DrawerDivider } from "../../layout/RightDrawer.style";
import { Settings } from "./settings/Settings";
import { Members } from "./members/Members";
import { Vitals } from "./vitals/Vitals";
import {
  LabRequisitionsProvider,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import {
  FEATURES,
  GROUP_TYPE,
  IBroadcastInfo,
  IGroupInfo,
} from "@home-health-notify/domain";
import { CommonGroupInfo } from "./info/common/CommonGroupInfo";
import { PatientGroupInfo } from "./info/patient/PatientGroupInfo";
import TabPanel from "../../common/tabs/TabPanel";
import { GroupHeaderTabsStyles, TabsContainer } from "./GroupHeaderTabs.style";
import CareTimeline from "../careTimeline/CareTimeline";
import LabRequisitions from "./labs/LabRequisitions";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export const GroupHeaderTabs = ({
  groupInfo,
  tabValue = 0,
}: {
  groupInfo: IGroupInfo | IBroadcastInfo;
  tabValue?: number;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const [value, setValue] = React.useState(tabValue);
  const { selectedOrgUser } = useContext(OrganizationContext);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const isPatientGroup = groupInfo.groupType === GROUP_TYPE.PATIENT;

  const isPendingReferral =
    isPatientGroup &&
    (groupInfo as IGroupInfo)?.pendingReferrals?.some(
      referral =>
        referral.userId === selectedOrgUser?.getOrganization().userProfile.id,
    );

  const enableSettingsTab =
    selectedOrgUser?.hasAccess(FEATURES.VIEW_GROUP_SETTINGS) &&
    !isPendingReferral;

  useEffect(() => {
    setValue(tabValue);
  }, [groupInfo.id, tabValue]);

  const enableLabsTab =
    selectedOrgUser!.isLabIntegrationEnabled() &&
    !!selectedOrgUser?.hasAccess(FEATURES.ACCESS_LAB_REQUISITIONS);

  const canViewCareTimeline =
    groupInfo.isMember &&
    isPatientGroup &&
    !!selectedOrgUser?.hasAccess(FEATURES.VIEW_CARE_TIMELINE);

  const canViewVitals =
    groupInfo.isMember &&
    isPatientGroup &&
    !!selectedOrgUser?.hasAccess(FEATURES.VIEW_VITALS_TAB);

  const tabs = [
    ...["INFO", "MEMBERS"],
    ...(enableLabsTab && groupInfo.isMember && isPatientGroup ? ["LABS"] : []),
    ...(canViewVitals ? ["VITALS"] : []),
    ...(canViewCareTimeline ? ["TIMELINE"] : []),
    ...(enableSettingsTab ? ["SETTINGS"] : []),
  ];

  return (
    <GroupHeaderTabsStyles data-testid="group-header-tabs">
      <TabsContainer>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="chat info tabs"
          justify-content="space-between"
          variant="scrollable"
          scrollButtons={true}
        >
          <Tab
            label={tNs("info")}
            {...a11yProps(0)}
            sx={{ overflow: "auto" }}
            data-testid="info-tab"
          />
          <Tab
            label={tNs("members")}
            {...a11yProps(1)}
            sx={{ overflow: "auto" }}
            data-testid="members-tab"
          />
          {enableLabsTab && groupInfo.isMember && isPatientGroup && (
            <Tab
              label={tNs("labs")}
              {...a11yProps(tabs.indexOf("LABS"))}
              sx={{ overflow: "auto" }}
              data-testid="labs-tab"
            />
          )}
          {canViewVitals && (
            <Tab
              label={tNs("vitals")}
              {...a11yProps(tabs.indexOf("VITALS"))}
              sx={{ overflow: "auto" }}
              data-testid="vitals-tab"
            />
          )}
          {canViewCareTimeline && (
            <Tab
              label={tNs("careTimeline.careTimeline")}
              {...a11yProps(tabs.indexOf("TIMELINE"))}
              sx={{ overflow: "auto" }}
              data-testid="caretimeline-tab"
            />
          )}
          {enableSettingsTab && (
            <Tab
              label={tNs("settings")}
              {...a11yProps(tabs.indexOf("SETTINGS"))}
              sx={{ overflow: "auto" }}
              data-testid="settings-tab"
            />
          )}
        </Tabs>
        <DrawerDivider />
      </TabsContainer>
      <TabPanel
        value={value}
        index={0}
        uniqueKey="group-header-tabs"
        renderOnClick={true}
      >
        {isPatientGroup ? (
          <PatientGroupInfo groupInfo={groupInfo as IGroupInfo} />
        ) : (
          <CommonGroupInfo groupInfo={groupInfo} />
        )}
      </TabPanel>
      <TabPanel
        value={value}
        index={1}
        uniqueKey="group-header-tabs"
        renderOnClick={true}
      >
        <Members groupInfo={groupInfo} />
      </TabPanel>
      {enableLabsTab && groupInfo.isMember && isPatientGroup && (
        <TabPanel
          value={value}
          index={tabs.indexOf("LABS")}
          uniqueKey="group-header-tabs"
          renderOnClick={true}
        >
          <LabRequisitionsProvider>
            <LabRequisitions groupInfo={groupInfo as IGroupInfo} />
          </LabRequisitionsProvider>
        </TabPanel>
      )}
      {canViewVitals && (
        <TabPanel
          value={value}
          index={tabs.indexOf("VITALS")}
          uniqueKey="group-header-tabs"
          renderOnClick={true}
        >
          <Vitals groupInfo={groupInfo as IGroupInfo} />
        </TabPanel>
      )}
      {canViewCareTimeline && (
        <TabPanel
          value={value}
          index={tabs.indexOf("TIMELINE")}
          uniqueKey="group-header-tabs"
          renderOnClick={true}
        >
          <CareTimeline />
        </TabPanel>
      )}
      {enableSettingsTab && (
        <TabPanel
          value={value}
          index={tabs.indexOf("SETTINGS")}
          uniqueKey="group-header-tabs"
          renderOnClick={true}
        >
          <Settings groupInfo={groupInfo as IGroupInfo} />
        </TabPanel>
      )}
    </GroupHeaderTabsStyles>
  );
};
